/**
 * voice-v8 — automatischer Standort.
 *
 * Hält den serverseitig gespeicherten Standort (für den Voicebot) automatisch
 * aktuell, SOLANGE die App offen ist → gedrosseltes PUT /api/location.
 *
 * Zwei Quellen (2026-06-30, Martin):
 *  - NATIVE App (iOS/Capacitor): das @capacitor/geolocation-Plugin = echtes
 *    CoreLocation-GPS. In der iOS-WKWebView funktioniert navigator.geolocation
 *    NICHT zuverlässig — darum MUSS hier das Plugin genutzt werden. Zugriff
 *    bewusst über das globale window.Capacitor (KEIN statischer Import), sonst
 *    würde der Web-/PWA-Build das native-only-Plugin nicht auflösen können.
 *  - Web/PWA: navigator.geolocation.watchPosition (enableHighAccuracy).
 *
 * An/Aus-Schalter (Standard AN) in localStorage. Beim App-Start startet der
 * Watcher nur, wenn die Standort-Permission bereits erteilt ist (kein
 * ungefragter Prompt beim Laden); der iOS-Permission-Prompt kommt erst, wenn
 * der User den Schalter in den Einstellungen aktiv umlegt.
 */

import { saveUserLocation } from "./connectors-api.js";

const AUTO_LOCATION_KEY = "voice-v8.auto-location";
const MIN_INTERVAL_MS = 90_000; // höchstens alle 90 s senden
const MIN_MOVE_M = 50; // … oder sobald > 50 m bewegt
/** Guete des zuletzt gesendeten Fixes — siehe handleFix. */
let lastAccuracy: number | null = null;

let webWatchId: number | null = null;
let nativeWatchId: string | null = null;
let lastSent = 0;
let lastLat = Number.NaN;
let lastLng = Number.NaN;

// --- Capacitor-Zugriff ohne statischen Import --------------------------------
interface CapPosition {
  coords?: { latitude?: number; longitude?: number; accuracy?: number };
}
interface CapGeolocation {
  checkPermissions?: () => Promise<{
    location?: string;
    coarseLocation?: string;
  }>;
  requestPermissions?: () => Promise<{
    location?: string;
    coarseLocation?: string;
  }>;
  watchPosition?: (
    opts: {
      enableHighAccuracy?: boolean;
      timeout?: number;
      maximumAge?: number;
    },
    cb: (pos: CapPosition | null, err?: unknown) => void,
  ) => Promise<string>;
  clearWatch?: (opts: { id: string }) => Promise<void>;
  // Ticket 777: Einzelabruf statt Dauerbeobachtung.
  getCurrentPosition?: (opts: {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
  }) => Promise<CapPosition>;
}
interface CapGlobal {
  isNativePlatform?: () => boolean;
  Plugins?: { Geolocation?: CapGeolocation };
}
function capacitor(): CapGlobal | null {
  return (window as unknown as { Capacitor?: CapGlobal }).Capacitor ?? null;
}
function isNative(): boolean {
  return Boolean(capacitor()?.isNativePlatform?.());
}
function nativeGeo(): CapGeolocation | null {
  return capacitor()?.Plugins?.Geolocation ?? null;
}

export function isAutoLocationEnabled(): boolean {
  try {
    return localStorage.getItem(AUTO_LOCATION_KEY) !== "0"; // Standard: an
  } catch {
    return true;
  }
}

function distanceMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) *
      Math.cos((bLat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * Soll ein neuer Fix verworfen werden, weil er deutlich schlechter ist als
 * ein noch frischer alter? (2026-08-29)
 *
 * ⚠️ Beim Start feuern ZWEI Einzelabrufe: der genaue (GPS, seit heute) und
 * der alte ohne `enableHighAccuracy`. Landet der grobe eine Sekunde später,
 * ersetzt er einen 10-Meter-Fix durch einen mit 5 km — genau der Schaden,
 * der die ganze Sache ausgelöst hat („Landsee statt Hollenthon"), nur von
 * innen erzeugt.
 *
 * Die Unterscheidung „schlechterer Sensor" gegen „er ist wirklich woanders"
 * macht der Vergleich mit der SUMME der Unschärfen: liegt der neue Punkt
 * weiter weg, als beide Messungen zusammen erklären können, ist es eine
 * echte Bewegung und zählt. Liegt er innerhalb, sagt der grobe Fix nichts,
 * was der feine nicht besser wüsste.
 *
 * Als eigene Funktion, weil eine Regel, die man nicht prüfen kann, keine
 * Regel ist.
 */
export function verwirfSchlechterenFix(m: {
  /** Wie lange der bisherige Fix schon steht. */
  alterMs: number;
  /** Abstand zwischen altem und neuem Punkt, in Metern. */
  versatzM: number;
  neueGenauigkeitM?: number | null;
  alteGenauigkeitM?: number | null;
}): boolean {
  // Nach zwei Minuten ist der alte Fix kein Gegenargument mehr.
  if (!(m.alterMs < 120_000)) return false;
  const neu = m.neueGenauigkeitM;
  const alt = m.alteGenauigkeitM;
  if (typeof neu !== "number" || typeof alt !== "number") return false;
  if (!Number.isFinite(neu) || !Number.isFinite(alt)) return false;
  // „Deutlich" schlechter — kleine Schwankungen sind normal.
  if (neu <= alt * 3) return false;
  // Weiter weg als beide Unschärfen zusammen erklären? Dann ist er umgezogen.
  return m.versatzM <= neu + alt;
}

// Gemeinsam: einen frischen Fix gedrosselt an den Server schicken.
async function handleFix(
  lat: number,
  lng: number,
  accuracy?: number,
  /**
   * ⚠️ Ein ausdrücklich angeforderter GENAUER Fix umgeht die Drossel.
   *
   * Sonst ginge genau der Fall verloren, um den es geht: Martin steht seit
   * einer Minute am selben Fleck, gespeichert ist ein Netz-Fix mit 5 km
   * Unschärfe, der neue GPS-Fix hat 10 m — `moved` ist klein, also hätte
   * die Drossel ihn verworfen. Er ist aber nicht „dasselbe nochmal",
   * sondern dieselbe Stelle mit 500-fach besserer Kenntnis.
   */
  erzwingen = false,
): Promise<void> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  const now = Date.now();
  const moved = Number.isFinite(lastLat)
    ? distanceMeters(lastLat, lastLng, lat, lng)
    : Number.POSITIVE_INFINITY;
  // Gedrosselt: nur senden, wenn spürbar bewegt ODER lange nichts geschickt.
  if (!erzwingen && now - lastSent < MIN_INTERVAL_MS && moved < MIN_MOVE_M) {
    return;
  }
  /*
   * ⚠️ Ein deutlich SCHLECHTERER Fix darf einen frischen guten nicht
   * ueberschreiben.
   *
   * Gemessen beim Einbau des genauen Fixes: beim Start feuern ZWEI
   * Einzelabrufe — der neue mit GPS und der alte ohne. Landet der grobe
   * eine Sekunde spaeter, ersetzt er einen 10-Meter-Fix durch einen mit
   * 5 km. Das ist genau der Schaden, der die ganze Sache ausgeloest hat
   * („Landsee statt Hollenthon"), nur von innen.
   *
   * Die Unterscheidung „schlechterer Sensor" gegen „er ist wirklich
   * woanders" macht der Vergleich mit der SUMME der Unschaerfen: liegt der
   * neue Punkt weiter weg, als beide Messungen zusammen erklaeren koennen,
   * ist es eine echte Bewegung und zaehlt. Liegt er innerhalb, sagt der
   * grobe Fix nichts, was der feine nicht besser wuesste.
   */
  if (
    !erzwingen &&
    verwirfSchlechterenFix({
      alterMs: now - lastSent,
      versatzM: moved,
      neueGenauigkeitM: accuracy,
      alteGenauigkeitM: lastAccuracy,
    })
  ) {
    return;
  }
  lastSent = now;
  lastLat = lat;
  lastLng = lng;
  lastAccuracy = typeof accuracy === "number" ? accuracy : null;
  try {
    await saveUserLocation({ lat, lng, accuracy });
  } catch {
    /* still — der manuelle Standort-Button bleibt als Rückfall */
  }
}

/**
 * Ticket 777 (2026-07-27, Martin: "Standort NICHT mehr dauerhaft in Echtzeit
 * erfassen"): Der Watcher lief bisher mit `enableHighAccuracy` — also echtem
 * GPS — durchgehend, solange die App offen war. Das ist neben dem Mikro
 * (#782) der zweite Dauerverbraucher; an einem Vormittag kamen 29 % zusammen.
 *
 * Martins Zielregel: Standort mitschreiben, wenn Audio laeuft bzw. etwas
 * aufgenommen wird — im Ruhezustand keine Updates. Genau dafuer gibt es seit
 * pre.2028 schon ein Signal: `voice-v8:mikro-bedarf`. Es meldet exakt die
 * Zeitraeume, in denen Vespera zuhoert (PTT, Warm-Mikro), und die
 * Tages-Aufnahme meldet sich ueber `voice-v8:recorder-state`.
 *
 * Damit eine Ortsfrage trotzdem sofort beantwortbar bleibt, holt
 * `maybeStartGeoWatch` beim Start EINEN Fix statt einer Dauerbeobachtung.
 */
let geoBedarfMikro = false;
let geoBedarfAufnahme = false;
let geoStopTimer: ReturnType<typeof setTimeout> | null = null;
const GEO_STOP_GRACE_MS = 20_000;

function geoBedarfPruefen(): void {
  const gebraucht = geoBedarfMikro || geoBedarfAufnahme;
  if (gebraucht) {
    if (geoStopTimer) {
      clearTimeout(geoStopTimer);
      geoStopTimer = null;
    }
    if (isAutoLocationEnabled()) startGeoWatch(false);
    return;
  }
  // Nachlauf: kurze Pausen zwischen zwei Zuegen sollen das GPS nicht
  // jedes Mal neu aushandeln lassen.
  if (geoStopTimer) return;
  geoStopTimer = setTimeout(() => {
    geoStopTimer = null;
    if (!geoBedarfMikro && !geoBedarfAufnahme) stopGeoWatch();
  }, GEO_STOP_GRACE_MS);
}

if (typeof window !== "undefined") {
  window.addEventListener("voice-v8:mikro-bedarf", (e) => {
    const d = (e as CustomEvent<{ aktiv?: boolean; grund?: string }>).detail;
    /*
     * ⚠️⚠️ 22.09.2026 — EIN VORGEWAERMTES MIKRO IST KEIN SPRECHEN.
     *
     * Der Kommentar unten rechnet damit, dass das Mikrofon-Signal KURZ ist
     * („dort ist sie kurz"). Seit pre.3151 waermt auch der Browser vor, und
     * zwar nach jeder Antwort — daraus wuerde ohne diese Zeile eine
     * Dauerbeobachtung des Standorts mit hoher Genauigkeit, solange die
     * Seite offen ist.
     *
     * Ein warmer Strom haelt nur die Leitung offen, damit der naechste
     * Druck schnell ist. Wer nicht spricht, wird nicht geortet.
     */
    if (d?.grund === "vorwaermen") return;
    geoBedarfMikro = d?.aktiv === true;
    geoBedarfPruefen();
  });
  // 2026-07-29 (Audit Paket D, Akku): Bis hierher hat eine laufende Aufnahme
  // die DAUERBEOBACHTUNG des Standorts angeworfen (watchPosition mit hoher
  // Genauigkeit). Bei "Immer aufzeichnen" laeuft die Aufnahme praktisch rund
  // um die Uhr — also lief auch die Ortung rund um die Uhr, obwohl der native
  // Teil ohnehin schon eine sparsame Spur im Fuenf-Minuten-Takt fuehrt.
  // Es war exakt der Verbraucher, den Ticket 777 abgeschafft hatte, nur ueber
  // einen anderen Ausloeser zurueckgekehrt.
  //
  // Fuer die Zeitachse in "Mein Alltag" genuegt ein gelegentlicher Fixpunkt.
  // Deshalb: waehrend der Aufnahme KEINE Dauerbeobachtung mehr, sondern alle
  // zehn Minuten eine einzelne Abfrage ohne hohe Genauigkeit (und mit
  // Zwischenspeicher, siehe holeStandortEinmal). Das Mikrofon-Signal (PTT,
  // Anruf) faehrt die genaue Ortung weiterhin hoch — dort ist sie kurz und
  // wirklich gebraucht.
  const AUFNAHME_ORT_TAKT_MS = 10 * 60_000;
  let aufnahmeOrtTimer: number | null = null;
  window.addEventListener("voice-v8:recorder-state", (e) => {
    const st = (e as CustomEvent<{ state?: string }>).detail?.state;
    const laeuft = st === "recording";
    if (laeuft && aufnahmeOrtTimer === null) {
      void holeStandortEinmal();
      aufnahmeOrtTimer = window.setInterval(() => {
        if (document.visibilityState !== "visible") return;
        void holeStandortEinmal();
      }, AUFNAHME_ORT_TAKT_MS);
    } else if (!laeuft && aufnahmeOrtTimer !== null) {
      window.clearInterval(aufnahmeOrtTimer);
      aufnahmeOrtTimer = null;
    }
    // Die Dauerbeobachtung haengt jetzt NUR noch am Mikrofon-Bedarf.
    geoBedarfAufnahme = false;
    geoBedarfPruefen();
  });
}

/**
 * Einmaliger Fix — fuer den Start, fuer Ortsfragen und fuer Zuhause.
 * Ohne Dauerbeobachtung.
 *
 * ## `genau` (2026-08-29)
 *
 * Martin: *„auf der Zuhauseseite steht immer noch Landsee als Standort, aber
 * ich bin gerade schon in Hollenthon. Wie gibt es das?"* — und danach:
 * *„Baue das bitte ein mit dem GPS. Das hat ja schon mal funktioniert."*
 *
 * Gemessen war der gespeicherte Punkt frisch (15:17), aber 5000 m unscharf;
 * Hollenthon lag im Unschärfekreis, 5,52 km vom Punkt entfernt. Kein
 * Benennungstrick rechnet das zurück — dafür braucht es einen echten Fix.
 *
 * ⚠️ Das ist KEIN Rückbau von Ticket 777. Dort ging es um die
 * DAUERBEOBACHTUNG: *„Standort NICHT mehr dauerhaft in Echtzeit erfassen"* —
 * `watchPosition` mit GPS lief, solange die App offen war, und kostete an
 * einem Vormittag 29 % Akku. Hier läuft das Funkgerät für einen einzelnen
 * Fix und geht wieder aus. Die Dauerbeobachtung bleibt an Mikro und
 * Aufnahme gebunden wie bisher.
 *
 * `maximumAge: 30_000` statt 300_000: ein halbminütiger Zwischenspeicher
 * spart das Aushandeln beim Hin- und Herwechseln, ist aber zu kurz, um eine
 * echte Ortsänderung zu verschlucken.
 */
/** Nähe-/Navi-Frage: vor dem Senden einen genauen Fix holen. */
export function brauchtFrischenStandort(text: string): boolean {
  return /nähe|naehe|nächste|naechste|navigier|zulassung|supermarkt|apotheke|tankstelle|\bwo\s+(ist|sind|finde)/i.test(
    text,
  );
}

export async function holeStandortEinmal(genau = false): Promise<void> {
  if (!isAutoLocationEnabled()) return;
  const opts = genau
    ? { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 }
    : { enableHighAccuracy: false, timeout: 20_000, maximumAge: 300_000 };
  const geo = nativeGeo();
  if (isNative() && geo?.getCurrentPosition) {
    try {
      const st = (await geo.checkPermissions?.())?.location ?? "prompt";
      if (st !== "granted") return;
      const pos = await geo.getCurrentPosition(opts);
      const c = pos?.coords;
      if (c?.latitude != null && c?.longitude != null) {
        await handleFix(c.latitude, c.longitude, c.accuracy, genau);
      }
    } catch {
      /* kein Fix — der manuelle Standort-Knopf bleibt */
    }
    return;
  }
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) return;
  // ⚠️ Als Zusage, nicht als Rueckruf: der Aufrufer soll die Anzeige erst
  // NACH dem Speichern erneuern, sonst zeigt sie den alten Ort weiter.
  await new Promise<void>((fertig) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void handleFix(
          pos.coords.latitude,
          pos.coords.longitude,
          pos.coords.accuracy,
          genau,
        ).finally(fertig);
      },
      () => fertig(),
      opts,
    );
  });
}

function stopGeoWatch(): void {
  if (
    webWatchId !== null &&
    typeof navigator !== "undefined" &&
    navigator.geolocation
  ) {
    navigator.geolocation.clearWatch(webWatchId);
  }
  webWatchId = null;
  const geo = nativeGeo();
  if (nativeWatchId !== null && geo?.clearWatch) {
    void geo.clearWatch({ id: nativeWatchId }).catch(() => {
      /* egal */
    });
  }
  nativeWatchId = null;
}

// Web-Watcher (navigator.geolocation) — Web/PWA.
function startWebWatch(): void {
  if (webWatchId !== null) return;
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) return;
  webWatchId = navigator.geolocation.watchPosition(
    (pos) =>
      handleFix(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
    () => {
      /* Permission/Timeout-Fehler still ignorieren */
    },
    { enableHighAccuracy: true, maximumAge: 60_000, timeout: 20_000 },
  );
}

// Nativer Watcher (Capacitor Geolocation = echtes GPS). Liefert false, wenn das
// Plugin NICHT vorhanden ist (→ Aufrufer fällt auf den Web-Watcher zurück).
// allowPrompt=true darf den iOS-Permission-Dialog auslösen.
async function startNativeWatch(allowPrompt: boolean): Promise<boolean> {
  const geo = nativeGeo();
  if (!geo?.watchPosition) return false; // Plugin nicht im Build → Web-Fallback
  try {
    let state = (await geo.checkPermissions?.())?.location ?? "prompt";
    if (state !== "granted") {
      if (!allowPrompt) return true; // nativ, aber ohne Prompt → nicht starten
      state = (await geo.requestPermissions?.())?.location ?? state;
      if (state !== "granted") return true; // abgelehnt
    }
    if (nativeWatchId !== null) return true; // läuft schon
    nativeWatchId = await geo.watchPosition(
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 60_000 },
      (pos, err) => {
        if (err || !pos?.coords) return;
        const { latitude, longitude, accuracy } = pos.coords;
        if (latitude == null || longitude == null) return;
        handleFix(latitude, longitude, accuracy);
      },
    );
    return true;
  } catch {
    return true; // nativ vorhanden, aber Fehler — KEIN Web-Fallback erzwingen
  }
}

/**
 * Startet den passenden Watcher. allowPrompt=true (nur bei explizitem
 * User-Toggle) darf den iOS-Permission-Dialog auslösen.
 */
export function startGeoWatch(allowPrompt = false): void {
  if (isNative()) {
    void startNativeWatch(allowPrompt).then((handled) => {
      if (!handled) startWebWatch();
    });
    return;
  }
  startWebWatch();
}

/** Schalter umlegen (Einstellungen): Wert merken + Watcher starten/stoppen. */
export function setAutoLocationEnabled(on: boolean): void {
  try {
    localStorage.setItem(AUTO_LOCATION_KEY, on ? "1" : "0");
  } catch {
    /* localStorage nicht verfügbar — Laufzeit-Verhalten gilt trotzdem */
  }
  if (on)
    startGeoWatch(true); // expliziter Wunsch → Prompt erlaubt
  else stopGeoWatch();
}

/**
 * Beim App-Start aufrufen: startet den Watcher nur, wenn AN und die Permission
 * bereits erteilt ist — so erscheint beim Laden kein ungefragter Standort-
 * Dialog (der Nutzer gibt den Standort über den Einstellungen-Knopf frei).
 */
export async function maybeStartGeoWatch(): Promise<void> {
  if (!isAutoLocationEnabled()) return;
  // Native: einmaliger iOS-Permission-Prompt beim Start — die Auto-Standort-
  // Funktion ist standardmäßig an, der User erwartet sie. iOS zeigt den Dialog
  // nur EINMAL; danach gilt die gespeicherte Entscheidung (kein Nerv-Prompt).
  if (isNative() && nativeGeo()) {
    // Ticket 777: KEINE Dauerbeobachtung mehr beim Start. Permission klaeren
    // (der Dialog kommt wie bisher genau einmal) und EINEN Fix holen, damit
    // eine Ortsfrage sofort beantwortbar ist. Laufend beobachtet wird erst,
    // wenn das Mikro gebraucht wird oder eine Aufnahme laeuft — siehe
    // geoBedarfPruefen().
    const geo = nativeGeo();
    void (async () => {
      try {
        let st = (await geo?.checkPermissions?.())?.location ?? "prompt";
        if (st !== "granted") {
          st = (await geo?.requestPermissions?.())?.location ?? st;
        }
        if (st === "granted") await holeStandortEinmal();
      } catch {
        /* egal */
      }
    })();
    return;
  }
  // Web: nur starten wenn Permission bereits granted (kein ungefragter Prompt).
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) return;
  try {
    const perms = (navigator as Navigator & { permissions?: Permissions })
      .permissions;
    if (!perms?.query) return; // ohne Permissions-API konservativ NICHT prompten
    const st = await perms.query({ name: "geolocation" });
    if (st.state !== "granted") return;
  } catch {
    return;
  }
  void holeStandortEinmal();
}
